/**
 * Per-guild toggles (Admin-Panel), one per server-sidebar item, grouped like
 * the sidebar. Stored as `moduleConfigs.disabledPlugins: string[]` — empty /
 * missing = everything on.
 *
 *   kind 'plugin' — the bot enforces it too (commands, dashboard actions,
 *                   events). Counted in "X / N Plugins aktiv".
 *   kind 'page'   — dashboard-only: hides the sidebar item; direct visits
 *                   show a placeholder instead of the page.
 *   needsConfig   — has a "configured" state (green marker); everything else
 *                   is ready whenever it's on.
 *   requires      — also hidden while that plugin is off (e.g. Ticket-Liste
 *                   without the Tickets module).
 *
 * Keep keys in sync with `apps/web-api/src/lib/pluginStatus.ts` (all keys)
 * and `apps/bot/plugins/Admin/lib/pluginToggles.js` (kind 'plugin' only).
 */
export const PLUGIN_GROUPS = [
  { key: 'overview', label: 'Übersicht' },
  { key: 'community', label: 'Community' },
  { key: 'live', label: 'Live' },
  { key: 'tools', label: 'Werkzeuge' },
  { key: 'module', label: 'Module' },
  { key: 'panels', label: 'Panels' },
  { key: 'admin', label: 'Verwaltung' },
] as const;

type GroupKey = (typeof PLUGIN_GROUPS)[number]['key'];

interface PluginDef {
  key: string;
  label: string;
  /** Route under /server/:guildId ('' = server dashboard), null = no page. */
  path: string | null;
  group: GroupKey;
  kind: 'plugin' | 'page';
  needsConfig: boolean;
  requires?: string;
  /** Shown under the toggle in the Admin-Panel when the label isn't enough. */
  hint?: string;
}

// Generic over the key so PluginKey below stays a union of literals.
const def = <K extends string>(
  key: K,
  label: string,
  path: string | null,
  group: GroupKey,
  kind: 'plugin' | 'page',
  extra: Partial<Omit<PluginDef, 'key'>> = {}
): PluginDef & { key: K } => ({ key, label, path, group, kind, needsConfig: false, ...extra });

export const PLUGINS = [
  def('dashboard', 'Dashboard', '', 'overview', 'page'),

  def('members', 'Mitglieder', '/members', 'community', 'page'),
  def('ticketList', 'Ticket-Liste', '/tickets', 'community', 'page', { requires: 'tickets' }),
  def('leaderboard', 'XP-Rangliste', '/leaderboard', 'community', 'page', { requires: 'leveling' }),
  def('moderation', 'Warnungen & Moderation', '/warnings', 'community', 'plugin', {
    hint: 'Warn-, Bann-, Kick-, Timeout-Commands + Aktionen',
  }),
  def('giveaways', 'Giveaways', '/giveaways', 'community', 'plugin'),

  def('voice', 'Voice-Belegung', '/voice', 'live', 'page'),
  def('messageRate', 'Nachrichten-Rate', '/message-rate', 'live', 'page'),
  def('voiceManager', 'Voice-Manager', null, 'live', 'plugin', { hint: '/vc-Commands + Voice-Aktionen' }),

  def('actions', 'Aktionen', '/actions', 'tools', 'page'),
  def('embed', 'Embed-Composer', '/embed', 'tools', 'plugin'),
  def('media', 'Media-Bibliothek', '/media', 'tools', 'page'),
  def('posts', 'Gepostete Nachrichten', '/posts', 'tools', 'page', {
    hint: 'Panels + Nachrichten nachträglich bearbeiten',
  }),
  def('music', 'Music', '/music', 'tools', 'plugin'),
  def('nsfw', 'NSFW-Panel', '/nsfw', 'tools', 'plugin'),

  def('tickets', 'Tickets', '/config/tickets', 'module', 'plugin', { needsConfig: true }),
  def('welcome', 'Welcome', '/config/welcome', 'module', 'plugin', { needsConfig: true }),
  def('leave', 'Leave', '/config/leave', 'module', 'plugin', { needsConfig: true }),
  def('leveling', 'Leveling', '/config/leveling', 'module', 'plugin', { needsConfig: true }),
  def('autoMod', 'AutoMod', '/config/automod', 'module', 'plugin', { needsConfig: true }),
  def('logging', 'Logging', '/config/logging', 'module', 'plugin', { needsConfig: true }),
  def('modMail', 'ModMail', '/config/modmail', 'module', 'plugin', { needsConfig: true }),
  def('tempChannels', 'Temp Channels', '/config/tempchannels', 'module', 'plugin', { needsConfig: true }),
  def('serverStats', 'Server-Stats', '/config/serverstats', 'module', 'plugin', { needsConfig: true }),
  def('notifications', 'Notifications', '/config/notifications', 'module', 'plugin', { needsConfig: true }),
  def('patchNotes', 'Patch Notes', '/config/patch-notes', 'module', 'plugin', { needsConfig: true }),

  def('rules', 'Regeln', '/config/panels/rules', 'panels', 'plugin', { needsConfig: true }),
  def('supportRules', 'Support-Regeln', '/config/panels/support-rules', 'panels', 'plugin', { needsConfig: true }),
  def('selfRoles', 'Self-Roles', '/config/panels/self-roles', 'panels', 'plugin', { needsConfig: true }),
  def('games', 'Games-Katalog', '/config/games-catalog', 'panels', 'plugin', { needsConfig: true }),

  def('permabans', 'Permabans', '/permabans', 'admin', 'plugin'),
  def('commandPerms', 'Command-Perms', '/command-perms', 'admin', 'plugin'),
  def('backups', 'Backups', '/backups', 'admin', 'plugin'),
  def('antiNuke', 'Anti-Nuke', '/protection', 'admin', 'plugin', {
    needsConfig: true,
    hint: 'Seite Server-Schutz — greift erst, wenn dort eingeschaltet',
  }),
  def('antiRaid', 'Anti-Raid', '/protection', 'admin', 'plugin', {
    needsConfig: true,
    hint: 'Seite Server-Schutz — greift erst, wenn dort eingeschaltet',
  }),
  def('access', 'Zugriff', '/access', 'admin', 'page', { hint: 'Leute + Vorlagen dieses Servers verwalten' }),
];

export type PluginKey = (typeof PLUGINS)[number]['key'];

/** Bot plugins only — the "X / N Plugins aktiv" counts use these. */
export const BOT_PLUGINS = PLUGINS.filter((p) => p.kind === 'plugin');

export function getDisabledPlugins(moduleConfigs: unknown): string[] {
  const list = (moduleConfigs as Record<string, unknown> | null | undefined)?.disabledPlugins;
  return Array.isArray(list) ? list.filter((k): k is string => typeof k === 'string') : [];
}

export function isPluginEnabled(moduleConfigs: unknown, key: PluginKey): boolean {
  return !getDisabledPlugins(moduleConfigs).includes(key);
}

/** Off itself, or its `requires` plugin is off. */
export function isHiddenByToggles(disabled: Set<string>, key: string): boolean {
  if (disabled.has(key)) return true;
  const p = PLUGINS.find((x) => x.key === key);
  return !!p?.requires && disabled.has(p.requires);
}
